/**
 * Search box + filter menu + active dot — the machinery lifted out of the
 * chat-preview sidebar so the Channels page uses the exact same control.
 *
 * The chat preview paints itself with the Telegram palette, which is not the
 * MUI theme, so the colours arrive through `palette`. The default is expressed
 * in MUI theme tokens, which `sx` resolves on its own.
 */

import React, { useState } from 'react';
import { Box, Divider, IconButton, InputBase, Menu, MenuItem, Typography } from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DbStatus } from '@/types/api';
import { DB_STATUS_COLORS, DB_STATUS_VALUES, dbStatusLabelKey } from '@/utils/dbStatus';
import {
  ChannelFilters,
  ChannelStatusFilter,
  ManuscriptFilter,
  MEDIA_CATEGORY_FILTERS,
  isChannelFilterActive,
} from '@/utils/channelFilters';

export interface FilterBarPalette {
  bg: string;
  fieldBg: string;
  fieldBorder: string;
  text: string;
  mutedText: string;
  menuBg: string;
  menuHoverBg: string;
  menuSelectedBg: string;
}

const MUI_PALETTE: FilterBarPalette = {
  bg: 'transparent',
  fieldBg: 'background.paper',
  fieldBorder: 'divider',
  text: 'text.primary',
  mutedText: 'text.secondary',
  menuBg: 'background.paper',
  menuHoverBg: 'action.hover',
  menuSelectedBg: 'action.selected',
};

const STATUS_GROUP: ChannelStatusFilter[] = ['all', 'sorted', 'unsorted', 'has_notes'];
const MANUSCRIPT_GROUP: ManuscriptFilter[] = ['all', 'only', 'excluded'];

/** Swatch colour for a status entry, or undefined when it has no swatch. */
const statusColor = (status: ChannelStatusFilter): string | undefined => {
  if (status === 'sorted') return DB_STATUS_COLORS.in_db;
  if (status === 'unsorted') return DB_STATUS_COLORS.not_in_db;
  if (status === 'all' || status === 'has_notes') return undefined;
  return DB_STATUS_COLORS[status as DbStatus];
};

interface ChannelFilterBarProps {
  filters: ChannelFilters;
  onChange: (filters: ChannelFilters) => void;
  palette?: FilterBarPalette;
}

const ChannelFilterBar: React.FC<ChannelFilterBarProps> = ({
  filters,
  onChange,
  palette = MUI_PALETTE,
}) => {
  const { t } = useTranslation('pages');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isActive = isChannelFilterActive(filters);
  const activeColor = statusColor(filters.status) ?? 'primary.main';

  const statusLabel = (status: ChannelStatusFilter): string =>
    STATUS_GROUP.includes(status)
      ? t(`channelFilters.status.${status}`)
      : t(dbStatusLabelKey(status as DbStatus));

  const itemSx = {
    fontSize: '0.875rem',
    gap: 1.5,
    '&.Mui-selected': { bgcolor: palette.menuSelectedBg },
    '&:hover': { bgcolor: palette.menuHoverBg },
  };

  const renderRow = (
    key: string,
    label: string,
    selected: boolean,
    onSelect: () => void,
    swatch?: string
  ) => (
    <MenuItem
      key={key}
      selected={selected}
      onClick={() => {
        onSelect();
        setAnchorEl(null);
      }}
      sx={itemSx}
    >
      <Box
        sx={{
          width: 10,
          height: 10,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: swatch ?? 'transparent',
        }}
      />
      <Box sx={{ flex: 1 }}>{label}</Box>
      {selected && <CheckIcon sx={{ fontSize: 16, color: palette.mutedText }} />}
    </MenuItem>
  );

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: palette.bg,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          bgcolor: palette.fieldBg,
          borderRadius: '20px',
          border: 1,
          borderColor: palette.fieldBorder,
          px: 1.5,
          py: 0.5,
        }}
      >
        <SearchIcon sx={{ color: palette.mutedText, mr: 1, fontSize: 20 }} />
        <InputBase
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder={t('channelFilters.searchPlaceholder')}
          fullWidth
          sx={{
            color: palette.text,
            fontSize: '0.875rem',
            '& .MuiInputBase-input::placeholder': {
              color: palette.mutedText,
              opacity: 1,
            },
          }}
        />
      </Box>

      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t('channelFilters.menuLabel')}
        sx={{
          color: isActive ? activeColor : palette.mutedText,
          position: 'relative',
          '&:hover': { bgcolor: palette.menuHoverBg },
        }}
      >
        <FilterListIcon fontSize="small" />
        {isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: activeColor,
            }}
          />
        )}
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              bgcolor: palette.menuBg,
              color: palette.text,
              minWidth: 200,
              border: 1,
              borderColor: palette.fieldBorder,
            },
          },
        }}
      >
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: palette.mutedText }}>
          {t('channelFilters.groups.status')}
        </Typography>

        {STATUS_GROUP.map((status) =>
          renderRow(
            status,
            statusLabel(status),
            filters.status === status,
            () => onChange({ ...filters, status }),
            statusColor(status)
          )
        )}

        <Divider sx={{ borderColor: palette.fieldBorder }} />

        {DB_STATUS_VALUES.map((status) =>
          renderRow(
            status,
            statusLabel(status),
            filters.status === status,
            () => onChange({ ...filters, status }),
            DB_STATUS_COLORS[status]
          )
        )}

        <Divider sx={{ borderColor: palette.fieldBorder }} />

        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: palette.mutedText }}>
          {t('channelFilters.groups.manuscript')}
        </Typography>

        {MANUSCRIPT_GROUP.map((manuscript) =>
          renderRow(
            `manuscript-${manuscript}`,
            t(`channelFilters.manuscript.${manuscript}`),
            filters.manuscript === manuscript,
            () => onChange({ ...filters, manuscript })
          )
        )}

        <Divider sx={{ borderColor: palette.fieldBorder }} />

        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: palette.mutedText }}>
          {t('channelFilters.groups.media')}
        </Typography>

        {renderRow(
          'media-all',
          t('mediaCategories.all'),
          filters.media === 'all',
          () => onChange({ ...filters, media: 'all' })
        )}

        {MEDIA_CATEGORY_FILTERS.map((media) =>
          renderRow(
            `media-${media}`,
            t(`mediaCategories.${media}`),
            filters.media === media,
            () => onChange({ ...filters, media })
          )
        )}

        {/* The counts behind these rows come from a cache an admin refreshes by
            hand, so a brand-new upload is invisible here until they do. */}
        <Typography
          variant="caption"
          sx={{ px: 2, py: 0.75, display: 'block', maxWidth: 260, color: palette.mutedText, whiteSpace: 'normal' }}
        >
          {t('channelFilters.mediaCacheNote')}
        </Typography>
      </Menu>
    </Box>
  );
};

export default ChannelFilterBar;
